import { FaceCullingMode, RenderLayer, } from '@skand/three-js-renderer';
import { Color, Matrix4, Quaternion, Vector2, Vector3 } from 'three';
import { lengthToString, triangulate } from '../../../Utils';
/**
 * Create the line batchables.
 *
 * @param vertices
 * @param color
 * @param position
 * @param rotation
 * @param scale
 * @returns
 */
function createLineBatchables(vertices, color, position, rotation, scale) {
    const lineEdges = [];
    for (let i = 0; i < vertices.length - 1; i++) {
        lineEdges.push(vertices[i]);
        lineEdges.push(vertices[i + 1]);
    }
    const batchables = [
        {
            type: 'lines',
            vertices: lineEdges,
            colors: lineEdges.map(() => color),
            position,
            rotation,
            scale,
            material: {
                type: 'basicOpaque',
                color: new Color(1, 1, 1),
                vertexColors: true,
            },
            renderLayer: RenderLayer.AfterPanorama,
        },
    ];
    return batchables;
}
/**
 * Create the polygon batchables.
 *
 * @param vertices
 * @param color
 * @param position
 * @param rotation
 * @param scale
 * @returns
 */
function createPolygonBatchables(vertices, color, position, rotation, scale) {
    const batchables = [];
    const lineEdges = [];
    for (let i = 0; i < vertices.length - 1; i++) {
        lineEdges.push(vertices[i]);
        lineEdges.push(vertices[i + 1]);
    }
    lineEdges.push(lineEdges[lineEdges.length - 1]);
    lineEdges.push(lineEdges[0]);
    // Outline
    batchables.push({
        type: 'lines',
        vertices: lineEdges,
        colors: lineEdges.map(() => color),
        position,
        rotation,
        scale,
        material: {
            type: 'basicOpaque',
            color: new Color(1, 1, 1),
            vertexColors: true,
        },
        renderLayer: RenderLayer.AfterPanorama,
    });
    // Fill
    batchables.push({
        type: 'polygon',
        vertices,
        colors: vertices.map(() => color),
        indices: triangulate(vertices),
        position,
        rotation,
        scale,
        material: {
            type: 'basicAlpha',
            color: new Color(1, 1, 1),
            alpha: 0.5,
            vertexColors: true,
            faceCulling: FaceCullingMode.None,
        },
        renderLayer: RenderLayer.AfterPanorama,
    });
    return batchables;
}
/**
 * Batched 3D sketch.
 */
export class Sketch {
    constructor(name, type, color, vertices, position, sprites, meshBatch, labelManager, signalDirty) {
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "color", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "vertices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "position", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "scale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sprites", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "batchables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "measurementUnit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visibleName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visibleMeasurements", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "meshBatch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "signalDirty", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = name;
        this.type = type;
        this.color = color.clone();
        this.vertices = vertices;
        this.position = position;
        this.rotation = new Quaternion();
        this.scale = new Vector3(1, 1, 1);
        this.sprites = sprites;
        this.batchables = [];
        this.measurementUnit = 'metric';
        this.labels = [];
        this.visible = false;
        this.visibleName = false;
        this.visibleMeasurements = false;
        this.meshBatch = meshBatch;
        this.labelManager = labelManager;
        this.signalDirty = signalDirty;
        // Create the batchables
        if (this.type === 'lines') {
            this.batchables = createLineBatchables(this.vertices, color, this.position, this.rotation, this.scale);
        }
        else if (this.type === 'polygon') {
            this.batchables = createPolygonBatchables(this.vertices, color, this.position, this.rotation, this.scale);
        }
    }
    async show() {
        if (!this.visible) {
            this.visible = true;
            for (const batchable of this.batchables) {
                this.meshBatch.addMesh(batchable);
            }
            for (const sprite of this.sprites) {
                sprite.setVisible(this.type !== 'polygon');
            }
            this.refreshLabels();
            this.signalDirty();
        }
        return this;
    }
    async hide() {
        if (this.visible) {
            this.visible = false;
            for (const batchable of this.batchables) {
                this.meshBatch.removeMesh(batchable);
            }
            for (const sprite of this.sprites) {
                sprite.setVisible(false);
            }
            this.refreshLabels();
            this.signalDirty();
        }
        return this;
    }
    async destroy() {
        await this.hide();
        for (const sprite of this.sprites) {
            sprite.dispose();
        }
    }
    isVisible() {
        return this.visible;
    }
    isPicked(pick) {
        for (const batchable of this.batchables) {
            if (pick.userData !== undefined && pick.userData === this.meshBatch.getPickingId(batchable)) {
                return true;
            }
        }
        for (const sprite of this.sprites) {
            if (sprite === pick.object) {
                return true;
            }
        }
        return false;
    }
    getBoundingRadius() {
        const transform = new Matrix4().compose(this.position, this.rotation, this.scale);
        const positions = this.vertices.map(vertex => vertex.clone().applyMatrix4(transform));
        if (positions.length === 1) {
            return 1;
        }
        let longestLength = 0;
        for (let i = 0; i < positions.length - 1; i++) {
            for (let j = i + 1; j < positions.length; j++) {
                const a = positions[i];
                const b = positions[j];
                longestLength = Math.max(longestLength, a.distanceTo(b));
            }
        }
        return longestLength;
    }
    getPosition(position) {
        if (!position)
            position = new Vector3();
        return position.copy(this.position);
    }
    getRotation(rotation) {
        if (!rotation)
            rotation = new Quaternion();
        return rotation.copy(this.rotation);
    }
    getScale(scale) {
        if (!scale)
            scale = new Vector3();
        return scale.copy(this.scale);
    }
    setPosition(position) {
        this.position.copy(position);
        this.refreshPoints();
        this.refreshLabels();
        this.signalDirty();
    }
    setRotation(rotation) {
        this.rotation.copy(rotation);
        this.refreshPoints();
        this.refreshLabels();
        this.signalDirty();
    }
    setScale(scale) {
        this.scale.copy(scale);
        this.refreshPoints();
        this.refreshLabels();
        this.signalDirty();
    }
    /**
     * Get the model-space vertices of the sketch.
     *
     * @returns
     */
    getVertices() {
        return this.vertices.map(vertex => vertex.clone());
    }
    /**
     * Get the geometry type of the sketch.
     *
     * @returns
     */
    getType() {
        return this.type;
    }
    /**
     * Get the current color of the sketch.
     *
     * @returns
     */
    getColor() {
        return this.color.clone();
    }
    /**
     * Set the color of the sketch.
     *
     * @param color
     */
    setColor(color) {
        this.color = color.clone();
        for (const batchable of this.batchables) {
            batchable.colors = batchable.colors.map(() => this.color);
        }
        for (const sprite of this.sprites) {
            sprite.applyMaterial({
                type: 'basicOpaque',
                color: this.color,
            });
        }
        this.signalDirty();
    }
    /**
     * Get the name of the sketch.
     * @returns
     */
    getName() {
        return this.name;
    }
    /**
     * Set the name of the sketch.
     *
     * @param name
     */
    setName(name) {
        this.name = name;
    }
    /**
     * Show measurements.
     */
    showMeasurements() {
        if (!this.visibleMeasurements) {
            this.visibleMeasurements = true;
            this.refreshLabels();
        }
    }
    /**
     * Hide measurements.
     */
    hideMeasurements() {
        if (this.visibleMeasurements) {
            this.visibleMeasurements = false;
            this.refreshLabels();
        }
    }
    /**
     * Show name.
     */
    showName() {
        if (!this.visibleName) {
            this.visibleName = true;
            this.refreshLabels();
        }
    }
    /**
     * Hide name.
     */
    hideName() {
        if (this.visibleName) {
            this.visibleName = false;
            this.refreshLabels();
        }
    }
    /**
     * Set the measurement unit.
     *
     * @param unit
     */
    setMeasurementUnit(unit) {
        this.measurementUnit = unit;
        this.refreshLabels();
    }
    /**
     * Check if the name label is visible.
     *
     * @returns
     */
    isNameVisible() {
        return this.visibleName && this.visible;
    }
    /**
     * Check if the measurement labels are visible.
     *
     * @returns
     */
    isMeasurementsVisible() {
        return this.visibleMeasurements && this.visible;
    }
    /**
     * Refresh the point sprites.
     */
    refreshPoints() {
        const transform = new Matrix4().compose(this.position, this.rotation, this.scale);
        for (let i = 0; i < this.sprites.length; i++) {
            const vertex = this.vertices[i];
            const sprite = this.sprites[i];
            sprite.setPositionECEF(vertex.clone().applyMatrix4(transform));
        }
    }
    /**
     * Refresh the label list.
     */
    refreshLabels() {
        // Clear all labels
        for (const label of this.labels) {
            this.labelManager.remove(label);
        }
        this.labels.length = 0;
        // Compute vertices
        const transform = new Matrix4().compose(this.position, this.rotation, this.scale);
        const vertices = this.vertices.map(vertex => vertex.clone().applyMatrix4(transform));
        const labelOffset = new Vector2(0, -20);
        if (this.isNameVisible()) {
            this.labels.push(this.labelManager.add(this.name, this.position, true, labelOffset));
        }
        if (this.isMeasurementsVisible()) {
            // Add line labels
            const edgePoints = [...vertices];
            if (this.type === 'polygon') {
                edgePoints.push(edgePoints[0]);
            }
            for (let i = 0; i < edgePoints.length - 1; i++) {
                const a = edgePoints[i];
                const b = edgePoints[i + 1];
                const distance = a.distanceTo(b);
                const string = lengthToString(distance, this.measurementUnit, 2);
                const position = a.clone().add(b).multiplyScalar(0.5);
                this.labels.push(this.labelManager.add(string, position, true));
            }
            // Add point labels
            for (let i = 0; i < vertices.length; i++) {
                const string = `P${i + 1}`;
                this.labels.push(this.labelManager.add(string, vertices[i], true, labelOffset));
            }
        }
    }
}
